import map from "lodash/map"
import isEmpty from "lodash/isEmpty"
import find from "lodash/find"
import get from "lodash/get"
import toString from "lodash/toString"
import { useEffect, useState } from "react"
import css from "classnames"
import { ReactSVG } from "react-svg"
import getLanguageRoute from "utils-lib/getLanguageRoute"
import getContentUrl from "utils-lib/getContentUrl"
import { feUrls, feUrlSubdirectory } from "utils-lib/builds"
import { handleEnterPress } from "utils-lib/handleEnterPress"
import { withAuthentication } from "hoc-lib/appAuthentication"
import { withLocalization } from "hoc-lib/localization"
import { isTeamsitePage } from "utils-lib/isTeamsitePage"
import { getWindow } from "utils-lib/getWindow"
import withRouter from "hoc-lib/router"
import { isMyWMApp } from "utils-lib/isMyWMApp"
import { isNextApp } from "utils-lib/isNextApp"
import { pushToAdobeDataLayer } from "utils-lib/analytics/adobeDataLayer"
import Popover from "component-lib/Base/components/Popover"
import CircularProgress from "component-lib/Base/components/CircularProgress"
import Box from "component-lib/Base/components/Box"
import { ACCOUNT_TYPE } from "constants-lib/accountTypes"
import { userType } from "constants-lib/authentication"
import { ButtonTextIcon, Button, Link } from "../../../../../Base"
import Login from "../../../../../Login/LoginContainer"

export const strings = {
  en_CA: {
    VIEW_MY_DASHBOARD: `VIEW MY DASHBOARD`,
    LOG_IN: `Log in`,
    LOG_OUT: `Log out`,
    WELCOME: `WELCOME`,
    HAVE_A_BILLING_ACCOUNT: `Have a billing account?`,
    LINK_ACCOUNT: `Link Account`,
    HELLO: `Hello, `,
    MY_WM: `My WM`,
    DASHBOARD: `Dashboard`,
    MY_PAYMENTS: `My Payments`,
    MY_SERVICES: `My Services`,
    ROLLOFF_REQUEST: `Roll-Off Request`,
    PAYMENT_METHODS: `Payment Methods`,
    MY_PREFERENCES: `My Preferences`,
    CHANGE_PASSWORD: `Change Password`,
  },
  fr_CA: {
    VIEW_MY_DASHBOARD: `VOIR MON TABLEAU DE BORD`,
    LOG_IN: `Connexion`,
    LOG_OUT: `Fermer la session`,
    WELCOME: `BIENVENUE`,
    HAVE_A_BILLING_ACCOUNT: `Vous avez un compte de facturation ? `,
    LINK_ACCOUNT: `Lier le compte`,
    HELLO: `Bonjour, `,
    MY_WM: `Mon WM`,
    DASHBOARD: `Tableau de bord`,
    MY_PAYMENTS: `Mes paiements`,
    MY_SERVICES: `Mes services`,
    ROLLOFF_REQUEST: `Demande de conteneur`,
    PAYMENT_METHODS: `Méthodes de paiement`,
    MY_PREFERENCES: `Mes préférences`,
    CHANGE_PASSWORD: `Changer le mot de passe`,
  },
}

const LoginPopover = ({
  analytics_key,
  isPopoverOpen,
  isLoggedIn,
  togglePopover,
  language,
  myWmMenuHeading,
  shouldShowRolloffLink,
  loginEmailPlaceholderText,
  loginPasswordPlaceholderText,
  loginButtonCta,
  forgotEmailPwText,
  continueAsGuestText,
  createProfileText,
  logoutCtaText,
  loginDropdownLabel,
  myWmDropdownLabel,
  logOut,
  strings,
  router,
  hasMasterBillType,
  myServices = {},
  customerSelections = {},
  userManageAccount = {},
  userAccount,
}) => {
  const ssrWindow = getWindow()
  const languageRoute = getLanguageRoute(language)
  const [showLoader, setShowLoader] = useState(isNextApp)

  useEffect(() => {
    preventBeforeUnload()
    setTimeout(() => setShowLoader(false), 500)
    return () => {
      ssrWindow.document.removeEventListener(`scroll`, () => {
        togglePopover(true)
      })
    }
    /*
      material ui is locking the body scroll,
      so need to make body scrollable and then toggle popover visibility when scrolled
    */
  }, [])

  useEffect(() => {
    if (userAccount?.userState === userType.LOGGING_OUT) {
      // show loader for logging out state to ease out the transition
      setShowLoader(true)
    }
    // close the popover if the user is logged in and the user state changes
    else if (isLoggedIn || userAccount?.userState === userType.LOGGED_IN) {
      isPopoverOpen && togglePopover(false)
    } else {
      setShowLoader(false)
    }
  }, [isLoggedIn, userAccount?.userState])

  useEffect(() => {
    const datePickerElement = ssrWindow.document.querySelectorAll(
      `[id^="date-picker-inline"]`,
    )
    if (typeof ssrWindow.onbeforeunload === `function`) {
      preventBeforeUnload()
    }
    if (
      ssrWindow.document.body.style.overflow === `hidden` &&
      isEmpty(datePickerElement) &&
      !ssrWindow.document.querySelector(".MuiList-root")
    ) {
      ssrWindow.document.body.style.overflow = `scroll`
    }
  })

  const LOGGED_IN_MENU_ITEMS = [
    {
      string: `DASHBOARD`,
      url: `MYWM_DASHBOARD`,
      isExternal: !isMyWMApp,
    },
    {
      string: `MY_PAYMENTS`,
      url: `MYWM_USER_MYPAYMENT_BILLING`,
      isExternal: !isMyWMApp,
    },
    {
      string: `MY_SERVICES`,
      url: `MYWM_USER_MYSERVICES`,
      isExternal: !isMyWMApp,
    },
    {
      string: `PAYMENT_METHODS`,
      url: `MYWM_PAYMENT_METHODS`,
      isExternal: !isMyWMApp,
    },
    {
      string: `MY_PREFERENCES`,
      url: `MYWM_MY_PREFERENCES`,
      // This specific link should have isExternal as false for aem-spa and wm-next because aem has pref center link, which should be internal handling.
      isExternal: isMyWMApp || isTeamsitePage(ssrWindow.location.pathname),
    },
    {
      string: `CHANGE_PASSWORD`,
      url: `MYWM_PROFILE`,
      isExternal: !isMyWMApp,
    },
    {
      string: `ROLLOFF_REQUEST`,
      url: `ROLLOFF_REQUEST_LINK`,
      isExternal: !isMyWMApp,
    },
  ]

  const selectedCustomerId =
    toString(get(myServices, `selectedCustomerId`, ``)) ||
    get(myServices, `selectedServiceObject.customerId`, ``)

  const selectedAccountDetails = find(
    get(userManageAccount, `userAccountDetails`, []),
    (account) => account.custAccountId === selectedCustomerId,
  )
  const serviceId = get(myServices, `selectedServiceObject.serviceId`, ``)
  const lob =
    get(selectedAccountDetails, `lineOfBusiness`, ``) ||
    get(myServices, `selectedServiceObject.lineOfBusiness`, ``)

  const serviceSelected = find(
    myServices?.services,
    (service) => service.ezpayId === selectedCustomerId,
  )
  const services = find(
    serviceSelected?.services,
    (service) => service.serviceId === serviceId,
  )
  const currentUserManageAccount = userManageAccount?.userAccountDetails?.find(
    (account) => account.custAccountId === selectedCustomerId,
  )

  const analyticsKey = `rolloff-empty-return-mywm-top-menu`

  const analyticsData = {
    clicks: {
      object_content: {
        customerId: selectedCustomerId,
        serviceAddress:
          customerSelections?.customerSearchedAddress?.formattedAddress,
        agreementType: get(currentUserManageAccount, `category`, null),
        gisIds: get(myServices, `bulkPickupEligibilityData.data.zoneId`, null),
        wasteStream: services?.wasteStreamDescription || null,
        isCod: serviceSelected?.isCod || false,
        serviceType: services?.serviceType || null,
        serviceDescription: services?.serviceDescription || null,
        onlineProfileType:
          myServices.uniqueCustIdAddressObject?.length > 1
            ? ACCOUNT_TYPE.MULTI
            : ACCOUNT_TYPE.SINGLE,
        lob,
      },
    },
  }

  const renderPopupMenu = () => (
    <Popover
      data-testid="Login-popover"
      id="login-popover"
      disableScrollLock
      aria-expanded={isPopoverOpen}
      aria-labelledby={isLoggedIn ? `` : `welcomeText`}
      anchorEl={ssrWindow.document.querySelector(`.Header__login-button`)}
      anchorOrigin={{
        vertical: `bottom`,
        horizontal: `right`,
      }}
      transformOrigin={{
        vertical: `top`,
        horizontal: `right`,
      }}
      open={isPopoverOpen}
      onClose={(e) => {
        if (isPopoverOpen) {
          e.stopPropagation()
          togglePopover(false)
        }
      }}
    >
      {isLoggedIn ? (
        <div>
          <div
            style={{
              width: languageRoute === `/ca/fr` ? `15rem` : `12rem`,
            }}
          >
            <div className="pt-2 pb-2 px-3 maax-font-weight-bold wm-link-light-green ">
              {myWmMenuHeading}
            </div>
            <div className="TopMenuItem__logged-in-menu-items  mt-0 mb-0 maax-font-weight-regular">
              {map(LOGGED_IN_MENU_ITEMS, (item) => {
                const isPaymentsItem = item.string === `MY_PAYMENTS`

                let hideForPayment = false

                if (isPaymentsItem && hasMasterBillType) {
                  hideForPayment = true
                }

                if (feUrls[item.url] === feUrls.ROLLOFF_REQUEST_LINK) {
                  if (!shouldShowRolloffLink) {
                    return null
                  }
                }
                return (
                  <div className="" key={item.string}>
                    {!hideForPayment && (
                      <Button
                        fontClass="maax-font-weight-regular"
                        overrideClass="has-focus text-nowrap text-left pt-2 pb-2 px-3"
                        onClick={() => {
                          const url = feUrlSubdirectory(item.url, languageRoute)
                          if (
                            feUrls[item.url] === feUrls.ROLLOFF_REQUEST_LINK &&
                            myServices.uniqueCustIdAddressObject?.length === 1
                          ) {
                            pushToAdobeDataLayer(analyticsKey, analyticsData)
                          }
                          // It should be internal handling for MyWM and and external handling for aem-spa and wm-next, unless when it is explicitly defined in isExternal.
                          if (
                            isTeamsitePage(ssrWindow.location.pathname) ||
                            item.isExternal
                          ) {
                            ssrWindow.location.pathname = url
                          } else {
                            router.push(url)
                          }
                          togglePopover(false)
                        }}
                        style={{
                          color: `#67696D`,
                        }}
                      >
                        {strings[item.string]}
                      </Button>
                    )}
                  </div>
                )
              })}
            </div>
            <div className="pt-2 pb-3 px-3">
              <Link
                analytics_key={analytics_key}
                className={`TopMenuItem__logout-button has-focus maax-font-weight-bold ${
                  language === `fr_CA` ? `fr_CA` : ``
                }`}
                onClick={() => {
                  logOut()

                  // close the popover when logging out to smooth the transition
                  togglePopover(false)
                }}
              >
                {logoutCtaText || strings.LOG_OUT}
              </Link>
            </div>
          </div>
        </div>
      ) : (
        <div className="LoginPopover" style={{ width: `23rem` }}>
          <Login
            prefixInputID="flyout"
            emailPlaceholderText={loginEmailPlaceholderText}
            passwordPlaceholderText={loginPasswordPlaceholderText}
            loginButtonCta={loginButtonCta}
            forgotEmailPwText={forgotEmailPwText}
            continueAsGuestText={continueAsGuestText}
            createProfileText={createProfileText}
            handleGuestButtonClick={() => {
              ssrWindow.location = `${getLanguageRoute(language)}/mywm/locate`
            }}
            hideContinueAsGuest
            aria-expanded={isPopoverOpen}
          />
        </div>
      )}
    </Popover>
  )

  const renderButton = (isLogin) => {
    const classNames = css(
      `wm-link-light-green maax-font-weight-regular has-focus`,
      {
        "Header__login-button-text-icon": isLogin,
      },
    )

    const style = {
      padding: 0,
    }

    if (!isLogin) {
      style.background = `transparent`
    }

    if (showLoader) {
      // smooth out initial load on wm-next since using SSR
      return (
        <Box width="6.75rem" display="flex" justifyContent="center">
          <CircularProgress size={20} className="mx-1" />
        </Box>
      )
    }

    return (
      <ButtonTextIcon
        analytics_key={analytics_key}
        testid="LoginPopover-Button"
        className={`${classNames} ${
          isLogin
            ? `Header__login-button d-flex align-items-center`
            : `d-flex justify-content-center align-items-center`
        }`}
        aria-haspopup={`${isLogin ? `true` : `false`}`}
        aria-controls={`${isLogin ? `login-popover` : ``}`}
        style={{
          ...style,
          // prevent content jump when using SSR
          // width: isNextApp && showLoader ? `7rem` : `inherit`,
          height: `45px`,
          fontSize: `1rem`,
        }}
        onClick={(e) => {
          if (isLogin) {
            togglePopover(true)
            e.stopPropagation()
          }
        }}
        onKeyPress={(e) => {
          e.stopPropagation()
          handleEnterPress(togglePopover(true))
        }}
        rightChildren={
          isLogin && (
            <ReactSVG
              beforeInjection={(svg) => {
                svg.setAttribute("aria-hidden", "true")
              }}
              wrapper="span"
              className={`Header__login-button-arrow py-2 react-svg ${
                isPopoverOpen
                  ? `Header__login-button-arrow--open`
                  : `Header__login-button-arrow--closed`
              }`}
              src={getContentUrl(
                "/content/dam/wm/icons/controls/20x20/green1/material-ui/arrow-up.svg",
              )}
            />
          )
        }
        ariaExpanded={isPopoverOpen}
        ariaLabel={!isLoggedIn ? loginDropdownLabel : myWmDropdownLabel}
      >
        {!isLoggedIn ? loginDropdownLabel : myWmDropdownLabel}
      </ButtonTextIcon>
    )
  }

  const preventBeforeUnload = () => {
    /* prevent beforeunload popup from firing when continue as guest is clicked */
    ssrWindow.onbeforeunload = null
  }

  return (
    <div className="LoginPopover ml-3" data-testid="LoginPopover">
      {renderButton(true)}
      {renderPopupMenu()}
    </div>
  )
}

// // Note: React 19 no-longer supports propTypes. Leaving this for reference.
// LoginPopoverLegacy.propTypes = {
//   analytics_key: PropTypes.string.isRequired,
//   isPopoverOpen: PropTypes.bool.isRequired,
//   isLoggedIn: PropTypes.bool.isRequired,
//   togglePopover: PropTypes.func.isRequired,
//   language: PropTypes.string.isRequired,
//   myWmMenuHeading: PropTypes.string.isRequired,
//   shouldShowRolloffLink: PropTypes.bool.isRequired,
//   loginEmailPlaceholderText: PropTypes.string.isRequired,
//   loginPasswordPlaceholderText: PropTypes.string.isRequired,
//   loginButtonCta: PropTypes.string.isRequired,
//   forgotEmailPwText: PropTypes.string.isRequired,
//   continueAsGuestText: PropTypes.string.isRequired,
//   createProfileText: PropTypes.string.isRequired,
//   logoutCtaText: PropTypes.string.isRequired,
//   loginDropdownLabel: PropTypes.string.isRequired,
//   myWmDropdownLabel: PropTypes.string.isRequired,
//   logOut: PropTypes.func.isRequired,
//   strings: PropTypes.object.isRequired,
//   router: PropTypes.object.isRequired,
//   hasMasterBillType: PropTypes.bool.isRequired,
//   myServices: PropTypes.object,
//   customerSelections: PropTypes.object,
//   userManageAccount: PropTypes.object,
// }

// // Note: React 19 no-longer supports defaultProps. Leaving this for reference.
// LoginPopoverLegacy.defaultProps = {
//   myServices: {},
//   customerSelections: {},
//   userManageAccount: {},
// }

export default withLocalization(strings)(
  withAuthentication(withRouter(LoginPopover)),
)
